import React, { useCallback, useMemo, useRef, useState } from "react";
import PlotlyPlot from "react-plotly.js";
import { ResizeSensor } from "@blueprintjs/core";
import classNames from "classnames";
import { defaultPlotConfig } from "shared/plotly/defaultConfigs";
import { RestyleYAxesDirection } from "shared/plotly/types";
import { getLayoutAxesRanges, checkIsAllAxesAutoscaled, checkIsAllGridlinesVisible, handleRestyleYAxes, } from "shared/plotly/helpers";
import { usePlotRelayout, useResetRangeInitial, useRoundTickValueObserver, useWheelOverlay, } from "shared/plotly/hooks";
import TextTruncate from "components/TextTruncate";
import GraphElementGraphActionMenu from "../GraphElementGraphActionMenu";
import * as styles from "./styles.module.scss";
const GraphElementGraph = ({ plotLayout, graphData, getActionMenuItems, onPlotHover, onCopy, onRelayout, renderHeader, renderConfig, className, }) => {
    const plotRef = useRef(null);
    const [plotRevisionNo, setPlotRevisionNo] = useState(0);
    const [axesRangesConfig, setAxesRangesConfig] = useState({});
    const [actionMenuOpen, setActionMenuOpen] = useState(false);
    const [actionMenuLeftOffset, setActionMenuLeftOffset] = useState(0);
    const [actionMenuItems, setActionMenuItems] = useState([]);
    const isAllAxesAutoscaled = useMemo(() => checkIsAllAxesAutoscaled(axesRangesConfig), [axesRangesConfig]);
    const isAllGridlinesVisible = useMemo(() => checkIsAllGridlinesVisible(axesRangesConfig), [axesRangesConfig]);
    const plotConfig = useMemo(() => (onRelayout ? defaultPlotConfig : { ...defaultPlotConfig, scrollZoom: false }), [onRelayout]);
    const xAxisTitle = useMemo(() => {
        const title = plotLayout.xaxis?.title;
        return typeof title === "string" ? title : (title?.text ?? "");
    }, [plotLayout.xaxis?.title]);
    const handlePlotHover = useCallback((event) => onPlotHover(plotRef)(event), [onPlotHover]);
    const { handleRelayout, handleAutoscaleAllAxesRanges, handleAutoscaleButtonClick, handleGridlinesButtonClick, } = usePlotRelayout(plotLayout, onRelayout);
    useRoundTickValueObserver(plotRef);
    const { setGraphDiv } = useResetRangeInitial(plotLayout);
    const { renderWheelOverlay, handlePlotWrapperWheel } = useWheelOverlay("graph", {
        root: styles.wheelOverlay,
    });
    const restyleYAxes = useCallback((layout, graphDiv) => handleRestyleYAxes(RestyleYAxesDirection.THREE_LEFT_THREE_RIGHT, layout, graphDiv, axesRangesConfig, setAxesRangesConfig), [axesRangesConfig]);
    const onGraphClick = useCallback((e) => {
        const offsetY = e.event.offsetY;
        // manually fit top section boundaries
        if (!!getActionMenuItems && offsetY >= 11 && offsetY <= 48) {
            setActionMenuOpen(true);
            setActionMenuLeftOffset(e.points[0].bbox.x0);
            setActionMenuItems(getActionMenuItems(e.points));
            return;
        }
        !!onCopy && onCopy(e);
    }, [getActionMenuItems, onCopy]);
    return (React.createElement(React.Fragment, null,
        renderHeader &&
            renderHeader(isAllAxesAutoscaled, isAllGridlinesVisible, handleAutoscaleAllAxesRanges, handleGridlinesButtonClick),
        React.createElement(ResizeSensor, { onResize: () => setPlotRevisionNo((prevState) => prevState + 1) },
            React.createElement("div", { className: classNames(styles.root, className), onWheelCapture: handlePlotWrapperWheel },
                React.createElement(GraphElementGraphActionMenu, { isOpen: actionMenuOpen, leftOffset: actionMenuLeftOffset, onSetIsOpen: setActionMenuOpen, menuItems: actionMenuItems }),
                onRelayout && renderWheelOverlay,
                React.createElement(PlotlyPlot, { revision: plotRevisionNo, ref: plotRef, config: plotConfig, data: graphData, layout: plotLayout ?? {}, className: styles.plot, onHover: handlePlotHover, onUnhover: () => handlePlotHover(), onRelayout: handleRelayout, onInitialized: (figure, graphDiv) => {
                        restyleYAxes(figure.layout, graphDiv);
                        setAxesRangesConfig(getLayoutAxesRanges(figure.layout, axesRangesConfig));
                        setGraphDiv(graphDiv);
                    }, onUpdate: (figure, graphDiv) => {
                        restyleYAxes(figure.layout, graphDiv);
                        setAxesRangesConfig(getLayoutAxesRanges(figure.layout, axesRangesConfig));
                        setGraphDiv(graphDiv);
                    }, onClick: onGraphClick }),
                React.createElement("div", { className: styles.xAxisTitle },
                    React.createElement(TextTruncate, { text: xAxisTitle })))),
        renderConfig && renderConfig(axesRangesConfig, handleAutoscaleButtonClick)));
};
export default GraphElementGraph;
